import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import React from "react";

export interface CollapsibleProps {
  title?: string;
  header?: React.ReactNode;
  children: React.ReactNode;

  defaultOpen?: boolean;
  /**
   * Optional controlled state.
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;

  disabled?: boolean;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  trailing?: React.ReactNode;
  // leading content, defaults to null
  leading?: React.ReactNode;
  // whether to show the chevron in the leading position, defaults to false
  leadingChevron?: boolean;
}

/** Module-level so parent re-renders (e.g. form onChange) do not remount and re-spin the chevron. */
function CollapsibleChevron({ isOpen }: { isOpen: boolean }) {
  return (
    <motion.span
      aria-hidden={isOpen ? "false" : "true"}
      aria-label={isOpen ? "Collapse section" : "Expand section"}
      initial={false}
      animate={{ rotate: isOpen ? 180 : 0 }}
      transition={{
        type: "spring",
        stiffness: 420,
        damping: 28,
        mass: 0.8,
      }}
      className={[
        "grid h-9 w-9 shrink-0 place-items-center rounded-full border-2",
        "border-[#E5E5E5] bg-[#F7F7F7] text-[#777777]",
        "shadow-[0_1px_0_#D0D0D0]",
      ].join(" ")}
    >
      <ChevronDown className="h-5 w-5" />
    </motion.span>
  );
}

export const Collapsible: React.FC<CollapsibleProps> = ({
  title,
  header,
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  disabled = false,
  className = "",
  headerClassName = "",
  contentClassName = "",
  trailing,
  leading = null,
  leadingChevron = false,
}) => {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const [animating, setAnimating] = React.useState(false);

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const toggle = () => {
    if (disabled) return;

    const nextOpen = !isOpen;

    if (!isControlled) {
      setInternalOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  };

  const hasCustomHeader = Boolean(header);
  const hasTitle = Boolean(title?.trim());

  const resolvedLeading =
    !leading && leadingChevron ? (
      <CollapsibleChevron isOpen={isOpen} />
    ) : (
      leading
    );

  return (
    <div
      className={[
        "w-full min-w-0 rounded-[1.35rem] border-2 border-[#E5E5E5] bg-white",
        "shadow-[0_5px_0_#D0D0D0]",
        // Clip only while closed/animating so nested dropdowns can escape when open.
        isOpen && !animating ? "overflow-visible" : "overflow-hidden",
        className,
      ].join(" ")}
    >
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        aria-expanded={isOpen}
        className={[
          "flex min-h-[56px] w-full min-w-0 items-center justify-between gap-3 px-4 py-3 text-left",
          "transition-colors hover:bg-[#F7F7F7]",
          "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#84D8FF]/50",
          "disabled:pointer-events-none disabled:opacity-50",
          headerClassName,
        ].join(" ")}
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center">
            <div className="flex-shrink-0">{resolvedLeading}</div>
            <div className="flex-grow justify-between items-center">
              {hasCustomHeader ? (
                header
              ) : hasTitle ? (
                <span className="block truncate text-base font-black text-[#3C3C3C]">
                  {title}
                </span>
              ) : (
                <span className="sr-only">Toggle section</span>
              )}
            </div>
          </div>
        </div>

        {trailing ? (
          trailing
        ) : leadingChevron ? (
          <></>
        ) : (
          <CollapsibleChevron isOpen={isOpen} />
        )}
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              height: {
                type: "spring",
                stiffness: 360,
                damping: 34,
                mass: 0.9,
              },
              opacity: {
                duration: 0.16,
                ease: "easeOut",
              },
            }}
            onAnimationStart={() => setAnimating(true)}
            onAnimationComplete={() => setAnimating(false)}
            className="w-full min-w-0"
            style={{ overflow: animating ? "hidden" : "visible" }}
          >
            <div
              className={[
                "w-full min-w-0 border-t-2 border-[#E5E5E5]",
                contentClassName,
              ].join(" ")}
            >
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Collapsible;
